// src/components/RoddoRadioMapWidget.tsx
import React from 'react';
import { useRoddoRadio } from '../context/RoddoRadioProvider';

interface RoddoRadioMapWidgetProps {
  isDarkMode?: boolean;
}

const stopMapEvent = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

const SpeakerGlyph: React.FC<{
  muted: boolean;
  medium: boolean;
}> = ({ muted, medium }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className="h-[23px] w-[23px]"
    fill="currentColor"
    stroke="none"
  >
    {/* Chunky speaker body */}
    <path d="M4.75 9.25A1.25 1.25 0 0 1 6 8h2.15l3.62-2.72A1.15 1.15 0 0 1 13.6 6.2v11.6a1.15 1.15 0 0 1-1.83.92L8.15 16H6a1.25 1.25 0 0 1-1.25-1.25v-5.5Z" />

    {muted ? (
      <>
        <rect x="16.2" y="8.2" width="1.9" height="9" rx="0.95" transform="rotate(45 16.2 8.2)" />
        <rect x="16.2" y="14.6" width="1.9" height="9" rx="0.95" transform="rotate(-45 16.2 14.6)" />
      </>
    ) : (
      <>
        <path d="M16.05 9.05a1.05 1.05 0 0 1 1.48.1 4.35 4.35 0 0 1 0 5.7 1.05 1.05 0 1 1-1.58-1.38 2.25 2.25 0 0 0 0-2.94 1.05 1.05 0 0 1 .1-1.48Z" />
        {!medium ? (
          <path d="M18.75 6.75a1.05 1.05 0 0 1 1.48.12 7.9 7.9 0 0 1 0 10.26 1.05 1.05 0 0 1-1.6-1.36 5.8 5.8 0 0 0 0-7.54 1.05 1.05 0 0 1 .12-1.48Z" />
        ) : null}
      </>
    )}
  </svg>
);

const RoddoRadioMapWidget: React.FC<RoddoRadioMapWidgetProps> = ({
  isDarkMode = false,
}) => {
  const { isPlaying, isMuted, volume } = useRoddoRadio();

  const effectiveVolume = isMuted ? 0 : volume;
  const muted = effectiveVolume <= 0;
  const medium = effectiveVolume > 0 && effectiveVolume < 0.45;

  const textColor = isDarkMode ? '#AEB8C9' : '#65727C';
  const shellBackground = isDarkMode ? '#101827' : '#FFF9F1';

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    window.dispatchEvent(
      new CustomEvent('roddo-radio-map-toggle', {
        detail: { source: 'map-sound-button', action: 'toggle' },
      }),
    );
  };

  return (
    <div
      className="pointer-events-auto relative overflow-visible"
      onMouseDown={stopMapEvent}
      onPointerDown={stopMapEvent}
      onTouchStart={stopMapEvent}
    >
      <button
        type="button"
        aria-label="Toggle Roddo Radio controls"
        title="Roddo Radio"
        onClick={handleToggle}
        className="roddo-tactile-hit group relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[15px] border backdrop-blur-xl transition-[transform,filter,box-shadow] duration-150 hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.97]"
        style={{
          background: shellBackground,
          borderColor: isDarkMode
            ? 'rgba(132,151,190,0.16)'
            : 'rgba(111,87,60,0.12)',
          color: textColor,
          boxShadow: isDarkMode
            ? '0 3px 10px rgba(0,0,0,0.16)'
            : '0 4px 10px rgba(89,65,37,0.07)',
        }}
      >
        <span className="relative z-10" aria-hidden="true">
          <SpeakerGlyph muted={muted} medium={medium} />
        </span>

        {isPlaying ? (
          <span
            className="absolute bottom-[5px] right-[5px] h-2 w-2 rounded-full border border-white/70 bg-[#34D399] shadow-sm"
            aria-hidden="true"
          />
        ) : null}
      </button>
    </div>
  );
};

export default RoddoRadioMapWidget;
